import { call, cancelled, delay, put, select, takeLatest, takeEvery } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { PartnerTierListType, PerformanceTrendAPIResponse, TrendChartKey, ConsentAPIResponse, SetConsentAPIResponse, SetConsentPayloadType } from "../../slices/partner-program/Types";
import { getPartnerTierListSagaAction, partnerTierSagaActionTypes, updatePartnerTierListSagaAction, deletePartnerTierList, addPartnerTierListPayloadType, updatePartnerTierListPayloadType, deletePartnerTierListPayloadType, PerformanceTrendPayloadType, getConsentStatusSagaAction, setConsentSagaAction } from "./saga-actions";
import { partnerTierSliceAction } from "../../slices/partner-program";

type getPartnerTierDeatilsListAPIResponseType = {
    success: boolean;
    message: string;
    data: PartnerTierListType;
};
const getAllPartnersTiersListAPI = async () => {
    let response = axiosRestInstance
        .get<AxiosResponse<getPartnerTierDeatilsListAPIResponseType>>(
            "/partner-program/tiers/getData"
        )
        .then((res) => res.data);
    return response;
};


type addPartnersTiersListAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const insertPartnersTiersAPI = async (data: addPartnerTierListPayloadType) => {
    let response = axiosRestInstance
        .post<AxiosResponse<addPartnersTiersListAPIResponseType>>(
            "/partner-program/tiers/insert", data)
        .then((res) => res.data);
    return response;
};


type updatePartnersTiersListAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const updatePartnerTierListAPI = async (partnerTier: updatePartnerTierListPayloadType) => {
  let response = axiosRestInstance
    .put<AxiosResponse<updatePartnersTiersListAPIResponseType>>(
        "/partner-program/tiers/update/" + partnerTier.ID,
        partnerTier
    )
    .then((res: { data: any; }) => res.data);
  return response;
};
type deletePartnerTierAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const deletePartnerTierAPI = async (partnerTierID: number) => {
  let response = axiosRestInstance
    .delete<AxiosResponse<deletePartnerTierAPIResponseType>>("/partner-program/tiers/" + partnerTierID)
    .then((res) => res.data);
  return response;
};

const getPerformanceTrendAPI = async (partnerId: number, range: string) => {
  let response = axiosRestInstance
    .get<AxiosResponse<PerformanceTrendAPIResponse>>(
      `/partner-program/points/performanceTrends/${partnerId}?range=${range}`
    )
    .then((res) => res.data);
  return response;
};

// Consent API functions
const getConsentStatusAPI = async (partnerId: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<ConsentAPIResponse>>(
      `/partner-program/getConsentSatus/${partnerId}`
    )
    .then((res) => res.data);
  return response;
};

const setConsentAPI = async (payload: SetConsentPayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<SetConsentAPIResponse>>(
      `/partner-program/setConsent`,
      payload
    )
    .then((res) => res.data);
  return response;
};

// ----------------------- API Above

const isPartnerProgramNotEligibleMessage = (message: string | undefined) =>
  Boolean(message && message.toLowerCase().includes("not eligible"));

/** Axios errors may put message on response.data.message or response.data as string */
const axiosErrorMessage = (e: any): string => {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  return d?.message ?? e?.message ?? "";
};

function* getAllPartnersTiersListSaga(action: {
    type: string;
    payload: {
        ID: number;
    };
}) {
    try {
        yield put(partnerTierSliceAction.setpartnerTierListLoading(true));
        let response: getPartnerTierDeatilsListAPIResponseType = yield call(
            getAllPartnersTiersListAPI,

        );
        const list = response.data ? response.data.reverse() : [];
        yield put(partnerTierSliceAction.setPartnerTierList(list));
        yield put(partnerTierSliceAction.setpartnerTierListLoading(false));
        yield put(partnerTierSliceAction.setFetchedPartnerTierID(action.payload.ID));
    } catch (e) {
        yield put(partnerTierSliceAction.setPartnerTierList([]));
        yield put(partnerTierSliceAction.setpartnerTierListLoading(false));
        console.log(e);
    }
}

function* insertPartnerTierListSaga(action: { type: string; payload: addPartnerTierListPayloadType }) {
  try {
    yield put(partnerTierSliceAction.setAddPartnerTierLoading(true));
    let response: addPartnersTiersListAPIResponseType = yield call(
      insertPartnersTiersAPI,
      action.payload
    );
    yield put(partnerTierSliceAction.setAddPartnerTierLoading(false));
    yield put(partnerTierSliceAction.setAddPartnerTierMessage(""));
    yield put(partnerTierSliceAction.setAddPartnerTierStatus(true));
    yield put(getPartnerTierListSagaAction(0));
  } catch (e: any) {
    yield put(
      partnerTierSliceAction.setAddPartnerTierMessage(
        e.response?.data?.message || "Something went wrong!"
      )
    );
    yield put(partnerTierSliceAction.setAddPartnerTierLoading(false));
    yield put(partnerTierSliceAction.setAddPartnerTierStatus(false));
    console.log(e);
  }
}

function* updatePartnerTierListSaga(action: {
  type: string;
  payload: { partnerTier: updatePartnerTierListPayloadType };
}) {
  try {
    yield put(partnerTierSliceAction.setUpdatePartnerTierLoading(true));
    yield put(partnerTierSliceAction.setUpdatePartnerTierMessage(""));

    const resp: { success: boolean; message: string; data: any } = yield call(
      updatePartnerTierListAPI,
      action.payload.partnerTier
    );

    if (resp?.success) {
      yield put(partnerTierSliceAction.setUpdatePartnerTierStatus(true));
      yield put(partnerTierSliceAction.setUpdatePartnerTierMessage(resp.message ?? "Updated"));
      yield put(
        partnerTierSliceAction.setPartnerTierListUpdate(action.payload.partnerTier)
      );
    } else {
      yield put(partnerTierSliceAction.setUpdatePartnerTierStatus(false));
      yield put(partnerTierSliceAction.setUpdatePartnerTierMessage(resp?.message ?? "Update failed"));
    }
    yield put(getPartnerTierListSagaAction(0));
  } catch (e: any) {
    yield put(partnerTierSliceAction.setUpdatePartnerTierStatus(false));
    yield put(partnerTierSliceAction.setUpdatePartnerTierMessage(e?.message ?? "Unexpected error"));
  } finally {
    yield put(partnerTierSliceAction.setUpdatePartnerTierLoading(false));
  }
}
function* deletePartnerTierListSaga(action: {
  type: string;
  payload: deletePartnerTierListPayloadType;
}) {
  try {
    yield put(partnerTierSliceAction.setDeletePartnerTierLoading(true));
    let response: deletePartnerTierAPIResponseType = yield call(
      deletePartnerTierAPI,
      action.payload.ID
    );
    yield put(partnerTierSliceAction.setDeletePartnerTierStatus(true));
    yield put(getPartnerTierListSagaAction(0));
    action.payload.setOpen(false);
  } catch (e: any) {
    yield put(partnerTierSliceAction.setDeletePartnerTierStatus(false));
    yield put(
      partnerTierSliceAction.setDeletePartnerTierMessage(
        e?.response?.data?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(partnerTierSliceAction.setDeletePartnerTierLoading(false));
  }
}

// Map chart key to the corresponding data property in API response
const chartKeyToDataKey: Record<TrendChartKey, keyof PerformanceTrendAPIResponse['data']['charts']> = {
  tierStatus: 'tierStatus',
  totalPoints: 'totalPoints',
  netNewLicenses: 'netNewLicenses',
  totalLicenses: 'totalLicenses',
};

function* getPerformanceTrendSaga(action: {
  type: string;
  payload: PerformanceTrendPayloadType;
}) {
  const { chartKey, partnerId, range } = action.payload;
  
  try {
    // Set loading state
    yield put(partnerTierSliceAction.setTrendLoading({ chartKey, loading: true }));
    yield put(partnerTierSliceAction.setTrendError({ chartKey, error: null }));
    yield put(partnerTierSliceAction.setTrendPeriod({ chartKey, period: range }));

    // Call API
    const response: PerformanceTrendAPIResponse = yield call(
      getPerformanceTrendAPI,
      partnerId,
      range
    );

    if (response?.success && response?.data) {
      const dataKey = chartKeyToDataKey[chartKey];
      const values = response.data.charts[dataKey];
      const labels = response.data.labels;

      yield put(partnerTierSliceAction.setTrendData({
        chartKey,
        labels,
        values,
      }));
    } else {
      yield put(partnerTierSliceAction.setTrendError({
        chartKey,
        error: response?.message || "Failed to fetch trend data",
      }));
    }
  } catch (e: any) {
    yield put(partnerTierSliceAction.setTrendError({
      chartKey,
      error: e?.response?.data?.message || e?.message || "Something went wrong",
    }));
    console.error("Performance trend fetch error:", e);
  } finally {
    yield put(partnerTierSliceAction.setTrendLoading({ chartKey, loading: false }));
  }
}

function* getConsentStatusSaga(action: {
  type: string;
  payload: { partnerId: number };
}) {
  try {
    yield put(partnerTierSliceAction.setConsentLoading(true));
    yield put(partnerTierSliceAction.setConsentError(null));
    
    const response: ConsentAPIResponse = yield call(
      getConsentStatusAPI,
      action.payload.partnerId
    );

    if (response?.success) {
      if (response.data?.length > 0) {
        const activeConsent = response.data.find(c => c.status === "Active") || response.data[0];
        yield put(partnerTierSliceAction.setConsentStatus(activeConsent));
        yield put(partnerTierSliceAction.setPartnerProgramConsentEligible(null));
      } else {
        yield put(partnerTierSliceAction.setConsentStatus(null));
      }
    } else if (isPartnerProgramNotEligibleMessage(response?.message)) {
      yield put(partnerTierSliceAction.setPartnerProgramConsentEligible(false));
      yield put(partnerTierSliceAction.setConsentStatus(null));
    } else {
      yield put(partnerTierSliceAction.setConsentStatus(null));
    }
  } catch (e: any) {
    const msg = axiosErrorMessage(e);
    if (isPartnerProgramNotEligibleMessage(msg)) {
      yield put(partnerTierSliceAction.setPartnerProgramConsentEligible(false));
    }
    yield put(partnerTierSliceAction.setConsentError(
      msg || "Failed to fetch consent status"
    ));
    yield put(partnerTierSliceAction.setConsentStatus(null));
    console.error("Get consent status error:", e);
  } finally {
    const wasCancelled: boolean = yield cancelled();
    if (!wasCancelled) {
      yield put(partnerTierSliceAction.setConsentLoading(false));
      yield put(partnerTierSliceAction.setConsentChecked(true));
    }
  }
}

function* setConsentSaga(action: {
  type: string;
  payload: SetConsentPayloadType;
}) {
  try {
    yield put(partnerTierSliceAction.setSetConsentLoading(true));
    yield put(partnerTierSliceAction.setConsentError(null));
    
    const response: SetConsentAPIResponse = yield call(
      setConsentAPI,
      action.payload
    );
    
    if (response?.success) {
      yield put(partnerTierSliceAction.setPartnerProgramConsentEligible(null));
      yield put(getConsentStatusSagaAction(action.payload.partner_id));
    } else {
      if (isPartnerProgramNotEligibleMessage(response?.message)) {
        yield put(partnerTierSliceAction.setPartnerProgramConsentEligible(false));
      }
      yield put(partnerTierSliceAction.setConsentError(
        response?.message || "Failed to update consent"
      ));
    }
  } catch (e: any) {
    const msg = axiosErrorMessage(e);
    if (isPartnerProgramNotEligibleMessage(msg)) {
      yield put(partnerTierSliceAction.setPartnerProgramConsentEligible(false));
    }
    yield put(partnerTierSliceAction.setConsentError(
      msg || "Failed to update consent"
    ));
    console.error("Set consent error:", e);
  } finally {
    yield put(partnerTierSliceAction.setSetConsentLoading(false));
  }
}

function* partnerTierListSaga() {
    yield takeLatest(partnerTierSagaActionTypes.callPartnerTierListSaga, getAllPartnersTiersListSaga);
    yield takeLatest(partnerTierSagaActionTypes.callAddPartnerTiersSaga, insertPartnerTierListSaga);
    yield takeLatest(partnerTierSagaActionTypes.callUpdatePartnerTierSaga, updatePartnerTierListSaga);
    yield takeLatest(partnerTierSagaActionTypes.callDeletePartnerTierSaga, deletePartnerTierListSaga);
    yield takeEvery(partnerTierSagaActionTypes.callPerformanceTrendSaga, getPerformanceTrendSaga);
    yield takeLatest(partnerTierSagaActionTypes.callGetConsentStatusSaga, getConsentStatusSaga);
    yield takeLatest(partnerTierSagaActionTypes.callSetConsentSaga, setConsentSaga);
}

export default partnerTierListSaga;